import { DateTime } from 'luxon';

/**
 * `iso` is a full ISO 8601 date and time with or without offset. The formats without time
 * (`dd/MM/yyyy`, `MM/dd/yyyy`, `yyyy-MM-dd`) take the time from a separate column.
 * Order matters: on a tie in detection, the earlier one wins (so `dd/MM` beats `MM/dd`).
 */
export const DATE_FORMATS = [
  'dd/MM/yyyy HH:mm',
  'MM/dd/yyyy HH:mm',
  'yyyy-MM-dd HH:mm',
  'iso',
  'dd/MM/yyyy',
  'MM/dd/yyyy',
  'yyyy-MM-dd',
] as const;
export type DateFormatId = (typeof DATE_FORMATS)[number];

export type ParseLaunchResult =
  | { ok: true; iso: string }
  | { ok: false; code: 'required' | 'dateInvalid' };

// Lenient luxon patterns: one or two digits for day, month and hour.
const LUXON_PATTERNS: Record<Exclude<DateFormatId, 'iso'>, string> = {
  'dd/MM/yyyy HH:mm': 'd/M/yyyy H:mm',
  'MM/dd/yyyy HH:mm': 'M/d/yyyy H:mm',
  'yyyy-MM-dd HH:mm': 'yyyy-M-d H:mm',
  'dd/MM/yyyy': 'd/M/yyyy',
  'MM/dd/yyyy': 'M/d/yyyy',
  'yyyy-MM-dd': 'yyyy-M-d',
};

const ISO_DATE_AND_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const ISO_WITH_OFFSET = /(Z|[+-]\d{2}(:?\d{2})?)$/i;
const TIME_VALUE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

const FORMATS_WITH_TIME = DATE_FORMATS.filter(
  (format) => format === 'iso' || format.includes('HH:mm')
);
const FORMATS_WITHOUT_TIME = DATE_FORMATS.filter(
  (format) => format !== 'iso' && !format.includes('HH:mm')
);

function isDateOnly(format: DateFormatId): boolean {
  return FORMATS_WITHOUT_TIME.includes(format);
}

/** The date (and time, when the format has it) of `value` in `zone`, or null. */
function parseDatePart(
  value: string,
  format: DateFormatId,
  zone: string
): DateTime | null {
  const trimmed = value.trim();

  let parsed: DateTime;
  if (format === 'iso') {
    if (!ISO_DATE_AND_TIME.test(trimmed)) return null;
    parsed = ISO_WITH_OFFSET.test(trimmed)
      ? DateTime.fromISO(trimmed, { setZone: true })
      : DateTime.fromISO(trimmed, { zone });
  } else {
    parsed = DateTime.fromFormat(trimmed, LUXON_PATTERNS[format], { zone });
  }
  return parsed.isValid ? parsed : null;
}

/**
 * The format that parses the most non-empty samples. `hasTimeColumn` says whether the time
 * comes from another column (date-only formats) or is part of the value. Null when nothing parses.
 */
export function detectDateFormat(
  samples: string[],
  hasTimeColumn: boolean
): DateFormatId | null {
  const values = samples.filter((sample) => sample.trim() !== '');
  const candidates = hasTimeColumn ? FORMATS_WITHOUT_TIME : FORMATS_WITH_TIME;

  let best: { format: DateFormatId; matched: number } | null = null;
  for (const format of candidates) {
    const matched = values.filter(
      (value) => parseDatePart(value, format, 'utc') !== null
    ).length;
    if (matched > 0 && (best === null || matched > best.matched)) {
      best = { format, matched };
    }
  }
  return best?.format ?? null;
}

/**
 * ISO 8601 with offset for the launch described by `value` (and `time` for the date-only
 * formats), read in `timezone`. An ISO value with its own offset keeps it. Never throws.
 */
export function parseLaunchDateTime(
  value: string,
  time: string,
  format: DateFormatId,
  timezone: string
): ParseLaunchResult {
  if (value.trim() === '') return { ok: false, code: 'required' };

  const date = parseDatePart(value, format, timezone);
  if (!date) return { ok: false, code: 'dateInvalid' };

  let launch = date;
  if (isDateOnly(format)) {
    if (time.trim() === '') return { ok: false, code: 'required' };

    const match = TIME_VALUE.exec(time.trim());
    if (!match) return { ok: false, code: 'dateInvalid' };
    const [hour, minute, second] = [match[1], match[2], match[3] ?? '0'].map(
      Number
    );
    if (hour > 23 || minute > 59 || second > 59) {
      return { ok: false, code: 'dateInvalid' };
    }
    launch = date.set({ hour, minute, second });
  }

  const iso = launch.toISO({ suppressMilliseconds: true });
  return iso ? { ok: true, iso } : { ok: false, code: 'dateInvalid' };
}
